import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useEmailStore } from '@/stores/useEmailStore';
import { Mail, ScanSearch, Check, AlertTriangle, Clock, TrendingDown, Frown } from 'lucide-react';
import { toast } from 'sonner';

const ALERT_ICONS = {
  inactive: Clock,
  drop_off: TrendingDown,
  high_pain: Frown,
  low_engagement: TrendingDown,
  missed_session: AlertTriangle,
  stalled_progress: AlertTriangle,
};

const SEVERITY_VARIANT = {
  low: 'secondary',
  medium: 'outline',
  high: 'destructive',
};

export function ClientIssuesTable() {
  const navigate = useNavigate();
  const { alerts, alertsLoading, fetchAlerts, scanIssues, resolveAlert, scanResult } = useEmailStore();
  const [typeFilter, setTypeFilter] = useState('all');
  const [severityFilter, setSeverityFilter] = useState('all');
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    const params = {};
    if (typeFilter !== 'all') params.alert_type = typeFilter;
    if (severityFilter !== 'all') params.severity = severityFilter;
    fetchAlerts(params);
  }, [fetchAlerts, typeFilter, severityFilter]);

  const handleScan = async () => {
    setScanning(true);
    try {
      const result = await scanIssues();
      toast.success(`Scan complete: ${result?.total_new_alerts || 0} new issues found`);
    } catch {
      toast.error('Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleSendEmail = (alert) => {
    const profile = alert.profiles;
    const params = new URLSearchParams({ to: alert.user_id });
    if (profile?.full_name) params.set('name', profile.full_name);
    if (profile?.email) params.set('email', profile.email);
    params.set('alert', alert.id);
    navigate(`/email-studio?${params.toString()}`);
  };

  const handleResolve = async (alertId) => {
    await resolveAlert(alertId);
    toast.success('Alert resolved');
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Client Issues</CardTitle>
            <CardDescription>Detected issues that may need attention.</CardDescription>
          </div>
          <Button onClick={handleScan} disabled={scanning} size="sm" variant="outline">
            <ScanSearch className="h-4 w-4 mr-2" />
            {scanning ? 'Scanning...' : 'Scan Now'}
          </Button>
        </div>
        <div className="flex gap-3 mt-3">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
              <SelectItem value="drop_off">Drop-off</SelectItem>
              <SelectItem value="high_pain">High pain</SelectItem>
              <SelectItem value="low_engagement">Low engagement</SelectItem>
            </SelectContent>
          </Select>
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="All severity" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All severity</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </CardHeader>
      <CardContent>
        {alertsLoading ? (
          <p className="text-sm text-muted-foreground py-8 text-center">Loading...</p>
        ) : alerts.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            No unresolved issues. Run a scan to detect new issues.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Issue</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>Detected</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {alerts.map((alert) => {
                const Icon = ALERT_ICONS[alert.alert_type] || AlertTriangle;
                const profile = alert.profiles;
                return (
                  <TableRow key={alert.id}>
                    <TableCell>
                      <p className="font-medium text-sm">{profile?.full_name || 'Unknown'}</p>
                      <p className="text-xs text-muted-foreground">{profile?.email}</p>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                        <div>
                          <p className="text-sm">{alert.title}</p>
                          {alert.description && (
                            <p className="text-xs text-muted-foreground">{alert.description}</p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={SEVERITY_VARIANT[alert.severity]}>
                        {alert.severity}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {new Date(alert.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleSendEmail(alert)}
                        >
                          <Mail className="h-3.5 w-3.5 mr-1" />
                          Email
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleResolve(alert.id)}
                        >
                          <Check className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
